<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { PaymentService } from '@/services/payment.services';
import { useIsSystemAdmin } from '@/composables/useIsSystemAdmin';
import RefundDialog from '@/view/payments/RefundDialog.vue';
import CommercePaymentConfig from './CommercePaymentConfig.vue';
import CommercePaymentDetail from './CommercePaymentDetail.vue';
import PaymentReconciliationPanel from '@/components/payments/PaymentReconciliationPanel.vue';

const props = defineProps<{ storeId: string }>();

const { t } = useI18n({ useScope: 'global' });
const router = useRouter();
const queryClient = useQueryClient();
const { isSystemAdmin } = useIsSystemAdmin();

const STATUS_OPTIONS = [
  'none',
  'pending',
  'totaly_paid',
  'partialy_paid',
  'cancel',
  'failed',
] as const;
const REFUND_STATUS_OPTIONS = [
  'none',
  'pending',
  'totaly_refunded',
  'partialy_refunded',
  'cancel',
  'failed',
] as const;
const PROCESSOR_OPTIONS = ['mock', 'market_pay', 'on_site', 'payfast'] as const;

// ── Mode d'affichage : 'card' | 'table' ─────────────────────────────────────────
const viewMode = ref<'card' | 'table'>('card');

// ── Filtres essentiels ──────────────────────────────────────────────────────────
const search = ref('');
const statusFilter = ref<string[]>([]);
const processorFilter = ref<string[]>([]);
const dateFrom = ref('');
const dateTo = ref('');

// ── Filtres avancés (panneau) ───────────────────────────────────────────────────
const moreFiltersOpen = ref(false);
const refundStatusFilter = ref<string[]>([]);
const minAmount = ref<number | null>(null);
const maxAmount = ref<number | null>(null);
const curFilter = ref('');
const taxRateIdFilter = ref('');

function toggleArrayFilter(list: string[], value: string) {
  const idx = list.indexOf(value);
  if (idx >= 0) list.splice(idx, 1);
  else list.push(value);
}
function clearAdvancedFilters() {
  refundStatusFilter.value = [];
  minAmount.value = null;
  maxAmount.value = null;
  curFilter.value = '';
  taxRateIdFilter.value = '';
}
const advancedFilterCount = computed(() => {
  let n = 0;
  if (refundStatusFilter.value.length) n++;
  if (minAmount.value !== null) n++;
  if (maxAmount.value !== null) n++;
  if (curFilter.value) n++;
  if (taxRateIdFilter.value) n++;
  return n;
});

// ── Tri ──────────────────────────────────────────────────────────────────────────
const sortBy = ref<'createdAt' | 'amount' | 'status' | 'updatedAt'>(
  'createdAt',
);
const sortOrder = ref<'ASC' | 'DESC'>('DESC');
function toggleSortOrder() {
  sortOrder.value = sortOrder.value === 'DESC' ? 'ASC' : 'DESC';
}

// ── Pagination ────────────────────────────────────────────────────────────────
const page = ref(1);
const PAGE_SIZES = [10, 20, 50] as const;
const pageSize = ref<(typeof PAGE_SIZES)[number]>(20);

watch(
  [
    search,
    statusFilter,
    processorFilter,
    dateFrom,
    dateTo,
    refundStatusFilter,
    minAmount,
    maxAmount,
    curFilter,
    taxRateIdFilter,
    sortBy,
    sortOrder,
    pageSize,
  ],
  () => {
    page.value = 1;
  },
  { deep: true },
);

const {
  data: paymentsData,
  isPending: paymentsLoading,
  isError: paymentsError,
} = useQuery({
  queryKey: computed(() => [
    'cdp-payments',
    props.storeId,
    page.value,
    pageSize.value,
    search.value,
    statusFilter.value,
    processorFilter.value,
    dateFrom.value,
    dateTo.value,
    refundStatusFilter.value,
    minAmount.value,
    maxAmount.value,
    curFilter.value,
    taxRateIdFilter.value,
    sortBy.value,
    sortOrder.value,
  ]),
  queryFn: async () => {
    const res = await PaymentService.searchPayments({
      page: page.value,
      pageSize: pageSize.value,
      sortBy: sortBy.value,
      sortOrder: sortOrder.value,
      search: search.value || undefined,
      status: statusFilter.value.length ? statusFilter.value : undefined,
      processor: processorFilter.value.length
        ? processorFilter.value
        : undefined,
      from: dateFrom.value || undefined,
      to: dateTo.value || undefined,
      refundStatus: refundStatusFilter.value.length
        ? refundStatusFilter.value
        : undefined,
      storeId: [props.storeId],
      minAmount: minAmount.value ?? undefined,
      maxAmount: maxAmount.value ?? undefined,
      cur: curFilter.value || undefined,
      taxRateId: taxRateIdFilter.value || undefined,
    });
    return res.data;
  },
  enabled: computed(() => !!props.storeId),
  refetchInterval: 15_000,
  refetchOnWindowFocus: true,
});

const payments = computed<any[]>(() => paymentsData.value?.data ?? []);
const paymentTotal = computed(() => paymentsData.value?.total ?? 0);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(paymentTotal.value / pageSize.value)),
);
const visiblePages = computed(() => {
  const total = totalPages.value;
  const cur = page.value;
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const start = Math.max(1, Math.min(cur - 2, total - 4));
  return Array.from({ length: Math.min(5, total) }, (_, i) => start + i);
});

// ── Helpers d'affichage ──────────────────────────────────────────────────────
function formatAmount(p: any) {
  const amount = p.amount ?? 0;
  const cur = p.cur ?? p.currencyOriginalCode ?? 'EUR';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: cur,
    }).format(amount);
  } catch {
    return `${amount} ${cur}`;
  }
}
function formatDate(d: string | null | undefined) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
function statusColor(status: string) {
  switch (status) {
    case 'totaly_paid':
      return '#10b981';
    case 'partialy_paid':
      return '#3b82f6';
    case 'pending':
      return '#f59e0b';
    case 'cancel':
      return '#94a3b8';
    case 'failed':
      return '#ef4444';
    default:
      return '#64748b';
  }
}
function statusLabel(status: string) {
  return t(`paymentsMgmt.status.${status}`);
}
function refundStatusColor(status: string) {
  switch (status) {
    case 'totaly_refunded':
      return '#8b5cf6';
    case 'partialy_refunded':
      return '#3b82f6';
    case 'pending':
      return '#f59e0b';
    case 'failed':
      return '#ef4444';
    default:
      return '#64748b';
  }
}
function refundStatusLabel(status: string) {
  return t(`paymentsMgmt.refundStatus.${status}`);
}
function hasActiveRefund(p: any) {
  return p.refundStatus && p.refundStatus !== 'none';
}
function isFullyRefunded(p: any) {
  return p.refundStatus === 'totaly_refunded';
}
function effectiveStatusColor(p: any) {
  return hasActiveRefund(p)
    ? refundStatusColor(p.refundStatus)
    : statusColor(p.status);
}
function effectiveStatusLabel(p: any) {
  return hasActiveRefund(p)
    ? refundStatusLabel(p.refundStatus)
    : statusLabel(p.status);
}
function processorLabel(p: string) {
  return t(`paymentsMgmt.processor.${p}`);
}

// ── Navigation détail / configuration ─────────────────────────────────────────
const selectedPaymentId = ref<string | null>(null);
function openDetail(p: any) {
  selectedPaymentId.value = p.id;
}
function closeDetail() {
  selectedPaymentId.value = null;
}

// ── Configuration paiement / réconciliation inline (sous le tableau, sans changer de page) ─────
const viewPanel = ref<'list' | 'config' | 'reconciliation'>('list');
const configOpen = computed(() => viewPanel.value === 'config');
const reconciliationOpen = computed(() => viewPanel.value === 'reconciliation');
function togglePanel(panel: 'config' | 'reconciliation') {
  viewPanel.value = viewPanel.value === panel ? 'list' : panel;
}

// ── Actions rapides (rembourser / resynchroniser) ─────────────────────────────
const refundDialogOpen = ref(false);
const refundTargetPayment = ref<any | null>(null);
const reconcilingId = ref<string | null>(null);
const actionMenuOpenId = ref<string | null>(null);

function toggleActionMenu(p: any) {
  actionMenuOpenId.value = actionMenuOpenId.value === p.id ? null : p.id;
}
function closeActionMenu() {
  actionMenuOpenId.value = null;
}
function openRefundDialog(p: any) {
  refundTargetPayment.value = p;
  refundDialogOpen.value = true;
  actionMenuOpenId.value = null;
}
function closeRefundDialog() {
  refundDialogOpen.value = false;
}
function onRefunded() {
  queryClient.invalidateQueries({ queryKey: ['cdp-payments'] });
}
const reconcileError = ref('');
let reconcileErrorTimer: ReturnType<typeof setTimeout> | null = null;
async function reconcile(p: any) {
  actionMenuOpenId.value = null;
  reconcilingId.value = p.id;
  reconcileError.value = '';
  try {
    await PaymentService.reconcilePayment(p.id);
    await queryClient.invalidateQueries({ queryKey: ['cdp-payments'] });
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    reconcileError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('paymentsMgmt.reconcileError');
    if (reconcileErrorTimer) clearTimeout(reconcileErrorTimer);
    reconcileErrorTimer = setTimeout(() => {
      reconcileError.value = '';
    }, 5000);
  } finally {
    reconcilingId.value = null;
  }
}
</script>

<template>
  <div
    class="pm"
    @click="
      moreFiltersOpen = false;
      closeActionMenu();
    "
  >
    <CommercePaymentDetail
      v-if="selectedPaymentId"
      :payment-id="selectedPaymentId"
      @close="closeDetail"
    />
    <template v-else>
      <!-- Header -->
      <div class="pm__header">
        <div>
          <h1 class="pm__title">{{ t('paymentsMgmt.title') }}</h1>
          <p class="pm__subtitle">{{ t('paymentsMgmt.subtitle') }}</p>
        </div>
        <div class="pm__header-actions">
          <button
            v-if="isSystemAdmin"
            class="pm__btn pm__btn--primary"
            :class="{ 'pm__btn--active': reconciliationOpen }"
            @click.stop="togglePanel('reconciliation')"
          >
            <i
              :class="
                reconciliationOpen ? 'pi pi-arrow-left' : 'pi pi-verified'
              "
            />
            {{
              reconciliationOpen
                ? t('paymentsMgmt.backToList')
                : t('paymentReconciliation.title')
            }}
          </button>
          <button
            class="pm__btn pm__btn--primary"
            :class="{ 'pm__btn--active': configOpen }"
            @click.stop="togglePanel('config')"
          >
            <i :class="configOpen ? 'pi pi-arrow-left' : 'pi pi-cog'" />
            {{
              configOpen
                ? t('paymentsMgmt.backToList')
                : t('paymentsMgmt.configureStore')
            }}
          </button>
        </div>
      </div>

      <!-- Configuration paiement inline, remplace la liste tant qu'elle est ouverte -->
      <div v-if="configOpen" class="pm__config-panel" @click.stop>
        <CommercePaymentConfig :store-id="storeId" />
      </div>

      <!-- Réconciliation inline, remplace la liste tant qu'elle est ouverte -->
      <div v-else-if="reconciliationOpen" class="pm__config-panel" @click.stop>
        <PaymentReconciliationPanel :store-id="storeId" />
      </div>

      <template v-else>
        <!-- Filters -->
        <div class="pm__filters">
          <div class="pm__filters-row">
            <div class="pm__search-wrap">
              <i class="pi pi-search pm__search-icon" />
              <input
                v-model="search"
                class="pm__search"
                :placeholder="t('paymentsMgmt.searchPlaceholder')"
              />
            </div>

            <input
              v-model="dateFrom"
              type="date"
              class="pm__date-input"
              :title="t('paymentsMgmt.from')"
            />
            <input
              v-model="dateTo"
              type="date"
              class="pm__date-input"
              :title="t('paymentsMgmt.to')"
            />

            <select v-model="sortBy" class="pm__select">
              <option value="createdAt">
                {{ t('paymentsMgmt.sortCreated') }}
              </option>
              <option value="updatedAt">
                {{ t('paymentsMgmt.sortUpdated') }}
              </option>
              <option value="amount">{{ t('paymentsMgmt.sortAmount') }}</option>
              <option value="status">{{ t('paymentsMgmt.sortStatus') }}</option>
            </select>
            <button
              class="pm__icon-btn"
              :title="t('paymentsMgmt.toggleOrder')"
              @click="toggleSortOrder"
            >
              <i
                :class="
                  sortOrder === 'DESC'
                    ? 'pi pi-sort-amount-down'
                    : 'pi pi-sort-amount-up'
                "
              />
            </button>

            <div class="pm__view-toggle">
              <button
                class="pm__view-btn"
                :class="{ 'pm__view-btn--active': viewMode === 'card' }"
                :title="t('paymentsMgmt.viewCards')"
                @click="viewMode = 'card'"
              >
                <i class="pi pi-th-large" />
              </button>
              <button
                class="pm__view-btn"
                :class="{ 'pm__view-btn--active': viewMode === 'table' }"
                :title="t('paymentsMgmt.viewTable')"
                @click="viewMode = 'table'"
              >
                <i class="pi pi-list" />
              </button>
            </div>
          </div>

          <div class="pm__filters-row">
            <div class="pm__chip-group">
              <span class="pm__chip-group-label">{{
                t('paymentsMgmt.status.label')
              }}</span>
              <button
                v-for="s in STATUS_OPTIONS"
                :key="s"
                class="pm__chip"
                :class="{ 'pm__chip--active': statusFilter.includes(s) }"
                :style="
                  statusFilter.includes(s)
                    ? {
                        borderColor: statusColor(s),
                        color: statusColor(s),
                        background: statusColor(s) + '1a',
                      }
                    : {}
                "
                @click="toggleArrayFilter(statusFilter, s)"
              >
                {{ statusLabel(s) }}
              </button>
            </div>
          </div>

          <div class="pm__filters-row">
            <div class="pm__chip-group">
              <span class="pm__chip-group-label">{{
                t('paymentsMgmt.processor.label')
              }}</span>
              <button
                v-for="p in PROCESSOR_OPTIONS"
                :key="p"
                class="pm__chip"
                :class="{ 'pm__chip--active': processorFilter.includes(p) }"
                @click="toggleArrayFilter(processorFilter, p)"
              >
                {{ processorLabel(p) }}
              </button>
            </div>

            <button
              class="pm__more-btn"
              :class="{ 'pm__more-btn--active': advancedFilterCount > 0 }"
              @click.stop="moreFiltersOpen = !moreFiltersOpen"
            >
              <i class="pi pi-sliders-h" />
              {{ t('paymentsMgmt.moreFilters') }}
              <span v-if="advancedFilterCount" class="pm__more-badge">{{
                advancedFilterCount
              }}</span>
            </button>

            <select v-model.number="pageSize" class="pm__select">
              <option v-for="ps in PAGE_SIZES" :key="ps" :value="ps">
                {{ ps }} {{ t('paymentsMgmt.perPage') }}
              </option>
            </select>

            <span class="pm__count"
              >{{ paymentTotal }} {{ t('paymentsMgmt.results') }}</span
            >
          </div>

          <!-- Panneau filtres avancés -->
          <Transition name="pm-panel">
            <div v-if="moreFiltersOpen" class="pm__advanced-panel" @click.stop>
              <div class="pm__advanced-grid">
                <div class="pm__field">
                  <label class="pm__label">{{
                    t('paymentsMgmt.refundStatus.label')
                  }}</label>
                  <div class="pm__chip-group pm__chip-group--wrap">
                    <button
                      v-for="rs in REFUND_STATUS_OPTIONS"
                      :key="rs"
                      class="pm__chip pm__chip--sm"
                      :class="{
                        'pm__chip--active': refundStatusFilter.includes(rs),
                      }"
                      @click="toggleArrayFilter(refundStatusFilter, rs)"
                    >
                      {{ t(`paymentsMgmt.refundStatus.${rs}`) }}
                    </button>
                  </div>
                </div>

                <div class="pm__field">
                  <label class="pm__label">{{
                    t('paymentsMgmt.amountRange')
                  }}</label>
                  <div class="pm__range-wrap">
                    <input
                      v-model.number="minAmount"
                      type="number"
                      class="pm__input"
                      :placeholder="t('paymentsMgmt.min')"
                    />
                    <span>—</span>
                    <input
                      v-model.number="maxAmount"
                      type="number"
                      class="pm__input"
                      :placeholder="t('paymentsMgmt.max')"
                    />
                  </div>
                </div>

                <div class="pm__field">
                  <label class="pm__label">{{
                    t('paymentsMgmt.currency')
                  }}</label>
                  <input
                    v-model="curFilter"
                    class="pm__input"
                    placeholder="EUR"
                  />
                </div>

                <div class="pm__field">
                  <label class="pm__label">{{
                    t('paymentsMgmt.taxRateId')
                  }}</label>
                  <input v-model="taxRateIdFilter" class="pm__input" />
                </div>
              </div>

              <div class="pm__advanced-actions">
                <button
                  class="pm__btn pm__btn--ghost"
                  @click="clearAdvancedFilters"
                >
                  {{ t('paymentsMgmt.clearFilters') }}
                </button>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Grid / Table -->
        <div v-if="paymentsLoading" class="pm__cards">
          <div v-for="i in 8" :key="i" class="pm__card pm__card--sk" />
        </div>
        <div v-else-if="paymentsError" class="pm__empty">
          <i class="pi pi-exclamation-circle" />
          <p>{{ t('paymentsMgmt.loadError') }}</p>
        </div>
        <div v-else-if="!payments.length" class="pm__empty">
          <i class="pi pi-wallet" />
          <p>{{ t('paymentsMgmt.empty') }}</p>
        </div>

        <div v-else-if="viewMode === 'card'" class="pm__cards">
          <div
            v-for="p in payments"
            :key="p.id"
            class="pm__card"
            @click="openDetail(p)"
          >
            <div class="pm__card-hd">
              <span class="pm__card-amount">{{ formatAmount(p) }}</span>
              <div class="pm__card-hd-right">
                <span
                  class="pm__pill"
                  :style="{
                    background: effectiveStatusColor(p) + '1a',
                    color: effectiveStatusColor(p),
                    borderColor: effectiveStatusColor(p) + '40',
                  }"
                >
                  {{ effectiveStatusLabel(p) }}
                </span>
                <div class="pm__menu-wrap" @click.stop>
                  <button class="pm__menu-btn" @click="toggleActionMenu(p)">
                    <i
                      v-if="reconcilingId === p.id"
                      class="pi pi-spin pi-spinner"
                    />
                    <i v-else class="pi pi-ellipsis-v" />
                  </button>
                  <Transition name="pm-drop">
                    <div
                      v-if="actionMenuOpenId === p.id"
                      class="pm__menu-panel"
                    >
                      <button
                        class="pm__menu-item"
                        :disabled="isFullyRefunded(p)"
                        @click="openRefundDialog(p)"
                      >
                        <i class="pi pi-replay" />
                        {{ t('paymentsMgmt.refund') }}
                      </button>
                      <button class="pm__menu-item" @click="reconcile(p)">
                        <i class="pi pi-sync" />
                        {{ t('paymentsMgmt.reconcile') }}
                      </button>
                    </div>
                  </Transition>
                </div>
              </div>
            </div>
            <span v-if="p.email" class="pm__card-email">{{ p.email }}</span>
            <div class="pm__card-ft">
              <span class="pm__card-processor">{{
                processorLabel(p.processor)
              }}</span>
              <span class="pm__card-date">{{ formatDate(p.createdAt) }}</span>
            </div>
          </div>
        </div>

        <div v-else class="pm__table-wrap">
          <table class="pm__table">
            <thead>
              <tr>
                <th>{{ t('paymentsMgmt.colAmount') }}</th>
                <th>{{ t('paymentsMgmt.colStatus') }}</th>
                <th>{{ t('paymentsMgmt.colProcessor') }}</th>
                <th>{{ t('paymentsMgmt.colEmail') }}</th>
                <th>{{ t('paymentsMgmt.colDate') }}</th>
                <th class="pm__th-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="p in payments"
                :key="p.id"
                class="pm__tr"
                @click="openDetail(p)"
              >
                <td class="pm__td-amount">{{ formatAmount(p) }}</td>
                <td>
                  <span
                    class="pm__pill"
                    :style="{
                      background: effectiveStatusColor(p) + '1a',
                      color: effectiveStatusColor(p),
                      borderColor: effectiveStatusColor(p) + '40',
                    }"
                  >
                    {{ effectiveStatusLabel(p) }}
                  </span>
                </td>
                <td>{{ processorLabel(p.processor) }}</td>
                <td>{{ p.email ?? '—' }}</td>
                <td class="pm__td-muted">{{ formatDate(p.createdAt) }}</td>
                <td class="pm__td-actions" @click.stop>
                  <div class="pm__menu-wrap">
                    <button class="pm__menu-btn" @click="toggleActionMenu(p)">
                      <i
                        v-if="reconcilingId === p.id"
                        class="pi pi-spin pi-spinner"
                      />
                      <i v-else class="pi pi-ellipsis-v" />
                    </button>
                    <Transition name="pm-drop">
                      <div
                        v-if="actionMenuOpenId === p.id"
                        class="pm__menu-panel"
                      >
                        <button
                          class="pm__menu-item"
                          :disabled="isFullyRefunded(p)"
                          @click="openRefundDialog(p)"
                        >
                          <i class="pi pi-replay" />
                          {{ t('paymentsMgmt.refund') }}
                        </button>
                        <button class="pm__menu-item" @click="reconcile(p)">
                          <i class="pi pi-sync" />
                          {{ t('paymentsMgmt.reconcile') }}
                        </button>
                      </div>
                    </Transition>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="pm__pagination">
          <button class="pm__pg-btn" :disabled="page === 1" @click="page = 1">
            <i class="pi pi-angle-double-left" />
          </button>
          <button class="pm__pg-btn" :disabled="page === 1" @click="page--">
            <i class="pi pi-chevron-left" />
          </button>
          <button
            v-for="p in visiblePages"
            :key="p"
            class="pm__pg-btn"
            :class="{ 'pm__pg-btn--active': p === page }"
            @click="page = p"
          >
            {{ p }}
          </button>
          <button
            class="pm__pg-btn"
            :disabled="page === totalPages"
            @click="page++"
          >
            <i class="pi pi-chevron-right" />
          </button>
          <button
            class="pm__pg-btn"
            :disabled="page === totalPages"
            @click="page = totalPages"
          >
            <i class="pi pi-angle-double-right" />
          </button>
        </div>

        <Transition name="pm-toast">
          <div v-if="reconcileError" class="pm__toast">
            <i class="pi pi-exclamation-circle" /> {{ reconcileError }}
          </div>
        </Transition>

        <RefundDialog
          :open="refundDialogOpen"
          :payment-id="refundTargetPayment?.id ?? null"
          :max-amount="refundTargetPayment?.amount ?? null"
          :currency="refundTargetPayment?.cur"
          @close="closeRefundDialog"
          @refunded="onRefunded"
        />
      </template>
    </template>
  </div>
</template>

<style scoped>
.pm {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.pm__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.pm__title {
  margin: 0 0 0.15rem;
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--nxt-on-surface);
}
.pm__subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}
.pm__header-actions {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}

/* Filters */
.pm__filters {
  position: relative;
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  padding: 1rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.pm__filters-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}
.pm__search-wrap {
  position: relative;
  flex: 1;
  min-width: 12rem;
}
.pm__search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.pm__search {
  width: 100%;
  height: 2.5rem;
  padding: 0 0.875rem 0 2.25rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
}
.pm__search:focus {
  border-color: #ffd60a;
}

.pm__date-input,
.pm__select,
.pm__input {
  height: 2.5rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}
.pm__input {
  font-weight: 400;
  cursor: text;
  width: 100%;
  box-sizing: border-box;
}

.pm__icon-btn,
.pm__view-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  cursor: pointer;
  flex-shrink: 0;
}
.pm__icon-btn {
  border-radius: 0.625rem;
}
.pm__icon-btn:hover {
  border-color: #ffd60a;
}
.pm__view-toggle {
  display: flex;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  overflow: hidden;
  flex-shrink: 0;
}
.pm__view-btn {
  border: none;
}
.pm__view-btn--active {
  background: #0b1320;
  color: #fff;
}
[data-theme='dark'] .pm__view-btn--active {
  background: #ffd60a;
  color: #0b1320;
}

/* Chips */
.pm__chip-group {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
}
.pm__chip-group--wrap {
  flex-wrap: wrap;
}
.pm__chip-group-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
  margin-right: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.pm__chip {
  padding: 0.3rem 0.75rem;
  border-radius: 9999px;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.75rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.pm__chip--sm {
  padding: 0.2rem 0.625rem;
  font-size: 0.6875rem;
}
.pm__chip--active {
  border-color: #ffd60a;
  background: rgba(255, 214, 10, 0.12);
  color: #0b1320;
  font-weight: 700;
}

.pm__more-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.pm__more-btn--active {
  border-color: #ffd60a;
  color: #0b1320;
}
.pm__more-badge {
  background: #ffd60a;
  color: #0b1320;
  border-radius: 9999px;
  min-width: 1.125rem;
  height: 1.125rem;
  font-size: 0.625rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.25rem;
}

.pm__count {
  margin-left: auto;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
}

/* Panneau filtres avancés */
.pm__advanced-panel {
  border-top: 1px solid var(--nxt-outline-variant);
  padding-top: 0.875rem;
}
.pm-panel-enter-active,
.pm-panel-leave-active {
  transition:
    opacity 0.2s,
    max-height 0.2s;
}
.pm-panel-enter-from,
.pm-panel-leave-to {
  opacity: 0;
}
.pm__advanced-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}
.pm__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.pm__label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.pm__range-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--nxt-on-surface-muted);
}
.pm__advanced-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
}
.pm__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.25rem;
  padding: 0 1rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
}
.pm__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.pm__btn--primary {
  background: #ffd60a;
  color: #0b1320;
}
.pm__btn--primary.pm__btn--active {
  background: #0b1320;
  color: #ffd60a;
}
[data-theme='dark'] .pm__btn--primary.pm__btn--active {
  background: rgba(255, 255, 255, 0.1);
  color: #ffd60a;
}

/* Panneau configuration paiement inline */
.pm__config-panel {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  padding: 1.25rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.pm__config-panel-hd {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
[data-theme='dark'] .pm__config-panel {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}

/* Grid & cards */
.pm__cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.25rem;
}
.pm__card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
  transition:
    box-shadow 0.3s cubic-bezier(0.2, 1, 0.3, 1),
    transform 0.3s cubic-bezier(0.2, 1, 0.3, 1);
}
.pm__card:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}
.pm__card--sk {
  height: 10rem;
  cursor: default;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: pm-shimmer 1.4s infinite;
}
@keyframes pm-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
.pm__card-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 0.5rem;
}
.pm__card-hd-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Menu d'actions rapides */
.pm__menu-wrap {
  position: relative;
}
.pm__menu-btn {
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 50%;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.75rem;
  cursor: pointer;
  flex-shrink: 0;
}
.pm__menu-btn:hover {
  border-color: #ffd60a;
}
.pm__menu-panel {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 200;
  min-width: 11rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  padding: 0.375rem;
}
.pm__menu-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 0.625rem;
  border: none;
  border-radius: 0.5rem;
  background: none;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--nxt-on-surface);
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
}
.pm__menu-item:hover {
  background: var(--nxt-surface-mid);
}
.pm__menu-item:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.pm__menu-item:disabled:hover {
  background: none;
}
.pm-drop-enter-active,
.pm-drop-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.pm-drop-enter-from,
.pm-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
.pm__th-actions,
.pm__td-actions {
  width: 2.5rem;
}

.pm__card-amount {
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.pm__card-email {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.pm__card-ft {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}

.pm__pill {
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.2rem 0.625rem;
  border-radius: 0.4rem;
  border: 1px solid;
  white-space: nowrap;
}

/* Table */
.pm__table-wrap {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 1.5rem;
  overflow: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
}
.pm__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}
.pm__table th {
  text-align: left;
  padding: 0.875rem 1rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 1px solid var(--nxt-outline-variant);
  white-space: nowrap;
}
.pm__tr {
  cursor: pointer;
  transition: background 0.15s;
}
.pm__tr:hover {
  background: var(--nxt-surface-mid);
}
.pm__tr td {
  padding: 0.875rem 1rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
  color: var(--nxt-on-surface);
  white-space: nowrap;
}
.pm__td-amount {
  font-weight: 700;
}
.pm__td-muted {
  color: var(--nxt-on-surface-muted);
}

.pm__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 4rem 1rem;
  color: var(--nxt-on-surface-muted);
}
.pm__empty i {
  font-size: 2rem;
  opacity: 0.4;
}

/* Pagination */
.pm__pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
}
.pm__pg-btn {
  min-width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-family: inherit;
  cursor: pointer;
}
.pm__pg-btn--active {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
  font-weight: 700;
}
.pm__pg-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.pm__toast {
  position: fixed;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.25rem;
  border-radius: 0.875rem;
  background: var(--nxt-error);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  max-width: 90vw;
}
.pm-toast-enter-active,
.pm-toast-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.pm-toast-enter-from,
.pm-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(0.5rem);
}
</style>

<style>
[data-theme='dark'] .pm__filters,
[data-theme='dark'] .pm__card,
[data-theme='dark'] .pm__table-wrap {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .pm__card:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
}
[data-theme='dark'] .pm__search,
[data-theme='dark'] .pm__date-input,
[data-theme='dark'] .pm__select,
[data-theme='dark'] .pm__input,
[data-theme='dark'] .pm__icon-btn,
[data-theme='dark'] .pm__view-btn,
[data-theme='dark'] .pm__more-btn,
[data-theme='dark'] .pm__chip {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .pm__search:focus {
  border-color: #ffd60a;
}
[data-theme='dark'] .pm__chip--active {
  background: rgba(255, 214, 10, 0.15);
  color: #ffd60a;
}
[data-theme='dark'] .pm__tr:hover {
  background: rgba(255, 255, 255, 0.04);
}
[data-theme='dark'] .pm__menu-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .pm__menu-panel {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .pm__menu-item:hover {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .pm__pg-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
</style>
